import React, { useMemo, useState } from 'react';
import {
  RACES, STRUCTURES, unitsByRace, structuresByRace, techChainForUnit, formatNum,
} from './sc2Data';

function producerLabel(unit) {
  const s = STRUCTURES[unit.producedFrom];
  let label = s ? s.ru : '—';
  if (unit.race === 'zerg' && unit.larva) label = `Личинка (${s.ru})`;
  if (unit.techLab) label += ' + Лаборатория';
  return label;
}

function UnitCard({ unit, expanded, onToggle }) {
  const chain = techChainForUnit(unit.id);
  return (
    <div className={`sc2-unit-card ${expanded ? 'expanded' : ''}`} onClick={onToggle}>
      <div className="sc2-unit-card-head">
        <div>
          <div className="sc2-unit-name">{unit.ru}</div>
          <div className="sc2-unit-name-en">{unit.name}</div>
        </div>
        <div className="sc2-unit-cost">
          <span className="cost-min" title="Минералы">💎 {unit.minerals}</span>
          {unit.gas > 0 && <span className="cost-gas" title="Газ">🟢 {unit.gas}</span>}
          <span className="cost-supply" title="Лимит">👥 {unit.supply}</span>
        </div>
      </div>
      <div className="sc2-unit-meta">
        <span>⏱ {unit.time}с{unit.warpTime ? ` (варп ${unit.warpTime}с)` : ''}</span>
        <span>🏭 {producerLabel(unit)}</span>
        {unit.reactor && <span className="sc2-badge reactor">Реактор ×2</span>}
        {unit.morphOf && <span className="sc2-badge morph">Морф</span>}
      </div>
      {expanded && (
        <div className="sc2-unit-details">
          <p className="sc2-unit-role">{unit.role}</p>
          <div className="sc2-unit-chain">
            <span className="sc2-chain-label">Технологии:</span>
            {chain.map((id, i) => (
              <React.Fragment key={id}>
                {i > 0 && <span className="sc2-chain-arrow">→</span>}
                <span className="sc2-chain-item">{STRUCTURES[id].ru}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UnitLibrary({ raceId }) {
  const [view, setView] = useState('units');
  const [expandedId, setExpandedId] = useState(null);
  const race = RACES[raceId];
  const units = useMemo(() => unitsByRace(raceId), [raceId]);
  const structures = useMemo(() => structuresByRace(raceId), [raceId]);

  return (
    <div>
      <h2 className="sc2-section-title" style={{ color: race.color }}>
        {race.icon} Библиотека: {race.ru}
      </h2>
      <div className="sc2-subtab-bar">
        <button className={`sc2-subtab ${view === 'units' ? 'active' : ''}`} onClick={() => setView('units')}>
          Юниты ({units.length})
        </button>
        <button className={`sc2-subtab ${view === 'structures' ? 'active' : ''}`} onClick={() => setView('structures')}>
          Здания ({structures.length})
        </button>
      </div>

      {view === 'units' && (
        <div className="sc2-unit-grid">
          {units.map((u) => (
            <UnitCard
              key={u.id}
              unit={u}
              expanded={expandedId === u.id}
              onToggle={() => setExpandedId(expandedId === u.id ? null : u.id)}
            />
          ))}
        </div>
      )}

      {view === 'structures' && (
        <table className="sc2-table">
          <thead>
            <tr>
              <th>Здание</th>
              <th>💎 Мин.</th>
              <th>🟢 Газ</th>
              <th>⏱ Время</th>
              <th>Требует</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {structures.map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{s.ru}</strong>
                  <div className="sc2-unit-name-en">{s.name}</div>
                </td>
                <td>{formatNum(s.minerals)}</td>
                <td>{s.gas > 0 ? formatNum(s.gas) : '—'}</td>
                <td>{s.time}с</td>
                <td>
                  {[...(s.morphFrom ? [`морф из: ${STRUCTURES[s.morphFrom].ru}`] : []),
                    ...(s.requires || []).map((r) => STRUCTURES[r].ru)].join(', ') || '—'}
                </td>
                <td>
                  {s.production && <span className="sc2-badge prod">производство</span>}
                  {s.supplyProvided && <span className="sc2-badge supply">+{s.supplyProvided} лимита</span>}
                  {s.addon && <span className="sc2-badge addon">аддон</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="sc2-note">
        Данные: LotV, патч 5.0.14. Время — реальные секунды на скорости «Faster». Клик по карточке юнита — детали и цепочка технологий.
      </p>
    </div>
  );
}

export default UnitLibrary;
